import { Col, Form, Row } from "react-bootstrap";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import PushPinIcon from "@mui/icons-material/PushPin";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import { appSettings } from "../../config/appSetting";
import { AgGridReact } from "ag-grid-react";
import { UserGridPreferenceModel } from "../../types/UserGridPreferenceModel";
import {
  getUserGridPreference,
  saveUserGridPreference,
} from "../../api/userDataApi/userGridPreferenceAPI";
import { useSnackbar } from "../Common/SnackbarContext";
import { FormatDate, FormatDateWithoutTime } from "../Common/CommonMethods";
import { ColDef } from "ag-grid-enterprise";
import { GetAPIIngestionData } from "../../api/apiingestionApi/apiingestionApi";
import LocalStorageUtil from "../../utils/localStorage";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import moment from "moment";
import CopyToClipboard from "react-copy-to-clipboard";
import styles from "./ApiIngestion.module.css";
import CloseIcon from "@mui/icons-material/Close";

/* LinkButtonRenderer component moved outside ElasticSearch component */
interface LinkButtonRendererProps {
  data: any;
  onClickOpen: (data: any) => void;
}

const LinkButtonRenderer: React.FC<LinkButtonRendererProps> = ({
  data,
  onClickOpen,
}) => {
  if (!data) {
    return null; // Return null instead of an empty string for cleaner JSX
  }

  return (
    <button
      type="button"
      style={{
        background: "none",
        border: "none",
        color: "var(--aggrid-text-color)",
        textDecoration: "underline",
        cursor: "pointer",
        padding: 0,
        textAlign: "left",
      }}
      onClick={() => onClickOpen(data)}
    >
      View RequestBody
    </button>
  );
};

const ApiIngestion: React.FC = (props) => {
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDDExceptionDefaulPageSize;
  const { showSnackbar } = useSnackbar();
  const gridRef = useRef<AgGridReact>(null);
  const [rowData, setRowData] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [gridRecordExist, setGridRecordExist] = useState(false);
  const [cacheBlockSize] = useState(10);
  const [, setTotalRecordsCount] = useState(0);
  const [gridApi] = useState<any>();
  const [searchContent, setsearchContent] = useState("");
  const [_searchContentError, set_SearchContentError] = useState<string>(""); //NoSONAR
  const [_showIconButton, set_ShowIconButton] = useState<boolean>(false); //NOSONAR
  const [showLoader, setShowLoader] = useState(false);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [_gridApi, set_GridApi] = useState<any>(); // NOSONAR
  const [open, setOpen] = useState(false);
  const [jsonPayloadValue, setJsonPayloadValue] = useState("");
  const [copyValue, setCopyValue] = useState(false);

  const handleClickOpen = (props: any) => {
    setJsonPayloadValue(props.requestBody);
    setOpen(true);
  };
  const handleClose = () => {
    setCopyValue(false);
    setOpen(false);
  };

  const handleCopyToClipboard = () => {
    setCopyValue(true);
  };

  useEffect(() => {
    resetGridData();
  }, [cacheBlockSize]);

  const resetGridData = () => {
    const now = new Date();
    const from = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 24 hours ago
    setStartDate(from);
    setEndDate(now);
    const params: any = {
      ContextName: LocalStorageUtil.getItem("contextName")?.toString() ?? "",
      CurrentPage: 0,
      PageSize: 0,
      SearchText: null,
      FromDate: from,
      ToDate: now,
      Timezone: appSettings.timezone,
    };
    getData(params);
  };
  const clearSelectionState = useCallback(() => {
    if (gridRef.current && rowData.length > 0) {
      gridRef.current.api.setServerSideSelectionState({
        selectAll: false,
        toggledNodes: [],
      });
    }
  }, []);

  const linkButtonCellRenderer = (params: { data: any }) => (
    <LinkButtonRenderer data={params.data} onClickOpen={handleClickOpen} />
  );

  //Rename the columns
  const renameColumns = (val: any) => {
    switch (val) {
      case "documentID":
        return "Document ID";
      case "cusip":
        return "CUSIP";
      case "uniqueID":
        return "Unique ID";
      case "docDate":
        return "Document Date";
      case "awsDate":
        return "AWS Date";
      case "associationID":
        return "Association ID";
      case "responseStatus":
        return "Response Status";
      case "modifiedDate":
        return "Modified Date";
      case "name":
        return "Name";
      case "url":
        return "URL";
      case "requestBody":
        return "Request Body";
      case "responseBody":
        return "Response Body";
      case "dfinCorrelationId":
        return "Dfin Correlation ID";
      case "dfinCorrelationSystem":
        return "Dfin Correlation System";
      default:
        return val.replace(/&amp;/, "&");
    }
  };

  const getUserGridPreferences = async () => {
    setShowLoader(true);
    const userGridPreference = {
      Area: "ApiIngestion",
      UserID: Number(LocalStorageUtil.getItem("userId")),
      BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };

    try {
      const docDistroCustomExceptionModel = await getUserGridPreference(
        userGridPreference
      );
      if (docDistroCustomExceptionModel) {
        showSnackbar("Grid state successfully saved.");
      }
      return docDistroCustomExceptionModel;
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setShowLoader(false);
    }
  };

  const getDynamicColumns = (
    response: any,
    columnsToExclude: string[],
    renameColumns: (val: any) => string
  ) => {
    return Object.keys(response[0])
      .filter((key) => !columnsToExclude.includes(key))
      .map((key: string) => {
        const renamed = renameColumns(key) || key;

        if (
          (key.toLowerCase().includes("date") &&
            key.toLowerCase() !== "aws date") ||
          key.toLowerCase() === "uploaded on"
        ) {
          return {
            headerName: renamed,
            field: key,
            sortable: true,
            filter: true,
            enableRowGroup: true,
            valueFormatter: (params: any) => {
              if (
                key.toLowerCase() === "supplement date" ||
                key.toLowerCase() === "revised date" ||
                key.toLowerCase() === "amendment date"
              ) {
                return FormatDateWithoutTime(params.value);
              }
              return FormatDate(params.value);
            },
          };
        } else {
          return {
            headerName: renamed,
            field: key,
            sortable: true,
            filter: true,
            enableRowGroup: true,
          };
        }
      });
  };

  // Helper to get combined column definitions
  const getCombinedColumnDefs = (dynamicColumns: any[]) => [
    {
      width: 10,
      sortable: false,
      filter: false,
      hide: false,
      colId: "colCheckbox",
      enableRowGroup: false,
      resizable: false,
    },
    {
      headerName: "#",
      width: 70,
      colId: "colRowNum",
      sortable: false,
      filter: false,
      resizable: false,
      enableRowGroup: false,
      valueGetter: (params: any) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
    },
    ...dynamicColumns,
    {
      headerName: "View RequestBody",
      field: "viewrequestbody",
      cellRenderer: linkButtonCellRenderer,
      pinned: "right",
      lockPinned: true,
      suppressSizeToFit: true,
      width: 160,
      minWidth: 160,
      hide: false,
      colId: "colJSONPayload",
      sortable: false,
      filter: false,
      resizable: false,
      enableRowGroup: false,
    },
  ];

  //Apply grid preferences
  const applyGridPreferences = (gridPreferences: any) => {
    if (gridPreferences) {
      try {
        const gridState = JSON.parse(gridPreferences);
        if (gridRef.current) {
          gridRef.current.api.applyColumnState({
            state: gridState,
            applyOrder: true,
          });
        }
      } catch (error) {
        console.error("Error parsing grid preferences:", error);
      }
    } else {
      console.warn("No grid preferences found or GridBlob is undefined.");
    }
  };

  const columnsToExclude = [
    "ingestionId",
    "headers",
    "workInProgressId",
    "modifiedUser",
  ];

  const getData = async (params: any) => {
    debugger;
    setShowLoader(true);
    try {
      const response = await GetAPIIngestionData(params);
      console.log("Fetched data:", response);

      const { RecordCounts, TableData } = response;

      if (response.length > 0) {
        const gridPreferences = await getUserGridPreferences();
        if (gridPreferences) {
          applyGridPreferences(gridPreferences);
          showSnackbar("Grid state applied successfully.");
        }

        // 1. Define your desired column order
        const predefinedOrder = [
          "documentID",
          "cusip",
          "uniqueID",
          "docDate",
          "awsDate",
          "associationID",
          "responseStatus",
          "modifiedDate",
          "name",
          "url",
          "requestBody",
          "responseBody",
          "dfinCorrelationId",
          "dfinCorrelationSystem",
        ];

        // 2. Sort dynamic columns according to predefined order
        const sortColumnsByPredefinedOrder = (columns: any[]) => {
          return columns.sort((a, b) => {
            const aIndex = predefinedOrder.indexOf(a.field);
            const bIndex = predefinedOrder.indexOf(b.field);
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
          });
        };

        // Generate all columns directly from the data
        const dynamicColumns = getDynamicColumns(
          response,
          columnsToExclude,
          renameColumns
        ); // empty exclude list
        const sortedDynamicColumns =
          sortColumnsByPredefinedOrder(dynamicColumns);
        const combinedColumnDefs = getCombinedColumnDefs(sortedDynamicColumns);

        setGridRecordExist(true);
        setRowData(response);
        setColumnDefs(combinedColumnDefs);
      } else {
        setColumnDefs([]);
        setRowData([]);
        setGridRecordExist(false);
      }
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setShowLoader(false);
    }
  };

  // Apply button click event
  const handleApplyButton = useCallback(
    async (e?: React.FormEvent) => {
      debugger;
      if (startDate && endDate && startDate > endDate) {
        showSnackbar("From date should be less than To date", true);
      } else {
        setTotalRecordsCount(0); // Reset total records count
        const params: any = {
          ContextName:
            LocalStorageUtil.getItem("contextName")?.toString() ?? "",
          CurrentPage: 0,
          PageSize: 0,
          SearchText: null,
          FromDate: formatDate(startDate),
          ToDate: formatDate(endDate),
          Timezone: appSettings.timezone,
        };

        await getData(params); // ✅ call getData

        if (gridApi) {
          clearSelectionState();
          gridApi.onFilterChanged();
        }
      }
    },
    [gridApi, clearSelectionState, startDate, endDate]
  );

  //handel reset button
  const handleReset = useCallback(
    async (e?: React.FormEvent) => {
      resetGridData();
      if (gridApi) {
        clearSelectionState();
        gridApi.onFilterChanged();
      }
    },
    [gridApi, clearSelectionState]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setsearchContent(event.target.value);
  };

  // Search content on enter key press
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };
  // Handel search button click
  const handleSearchSubmit = useCallback(
    async (e?: React.FormEvent) => {
      try {
        if (searchContent.trim().length > 0) {
          set_SearchContentError("");
          setTotalRecordsCount(0);
          const params: any = {
            ContextName:
              LocalStorageUtil.getItem("contextName")?.toString() ?? "",
            CurrentPage: 0,
            PageSize: 0,
            SearchText: searchContent,
            FromDate: formatDate(startDate),
            ToDate: formatDate(endDate),
            Timezone: appSettings.timezone,
          };

          await getData(params);
        } else {
          set_SearchContentError("Please enter search content");
        }

        if (gridApi) {
          clearSelectionState();
          gridApi.onFilterChanged();
        }
      } catch (error) {
        console.log(error);
      }
    },
    [searchContent, gridApi, clearSelectionState, startDate, endDate]
  );

  // Handel search content clear
  const handelSearchContentClear = () => {
    setsearchContent("");
    set_SearchContentError("");
    set_ShowIconButton(false);
    setTotalRecordsCount(0);
    try {
      resetGridData();
      if (gridApi) {
        clearSelectionState();
        gridApi.onFilterChanged();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: appSettings.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const formatDate = (date: any) => {
    if (!date) return null;
    const parts = formatter.formatToParts(date).reduce((acc: any, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
    return `${parts.month}-${parts.day}-${parts.year}`;
  };

  //Save grid preferences
  const handleSaveGridPreferences = async () => {
    setShowLoader(true);
    if (gridRef.current) {
      const gridState = gridRef.current.api.getColumnState();
      const userGridPreference: UserGridPreferenceModel = {
        Area: "ApiIngestion",
        UserID: Number(LocalStorageUtil.getItem("userId")),
        BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
        ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
        GridBlob: JSON.stringify(gridState),
        LastModifiedBy: "DocDistro-UserGridPreferencesAPI",
        LastModifiedDate: new Date().toISOString(),
      };

      try {
        const docDistroCustomExceptionModel = await saveUserGridPreference(
          userGridPreference
        );
        if (docDistroCustomExceptionModel) {
          showSnackbar("Grid state successfully saved.");
        }
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        setShowLoader(false);
      }
    }
  };

  //Handle Export to excel
  const handleExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsExcel({
        fileName:
          "ApiIngestionReports_" + moment().format("YYYYMMDD") + ".xlsx",
        sheetName: "ApiIngestion Report",
        columnKeys: columnDefs
          .map((colDef) => colDef.field)
          .filter((field): field is string => typeof field === "string"), // NOSONAR
        processCellCallback: function (params) {
          return params.value;
        },
      });
    }
  };

  return (
    <div style={{ overflow: "hidden" }}>
      <NavigationBar
        menuTitle="Activity"
        screenTitle=""
        title="Api Ingestion"
        text="Api Ingestion"
      />
      <Row style={{ marginLeft: "1em" }}>
        <Col>
          <Box>
            <div className="rp-main-inner col-sm-12">
              <div className="queue-top row clearfix">
                <div
                  className="col-sm-12"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: ".5em",
                  }}
                >
                  {/* Dates, Apply and Reset buttons */}
                  <div
                    className="col-sm-11"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: ".5em",
                    }}
                  >
                    <InputLabel
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        color: "var(--text-color)",
                        display: "inline-block !important",
                        marginBottom: "5px !important",
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      From Date: *
                    </InputLabel>
                    <Form.Control
                      type="date"
                      placeholder="From Date"
                      name="fromdate"
                      max={new Date().toISOString().split("T")[0]}
                      value={
                        startDate ? startDate.toISOString().split("T")[0] : ""
                      }
                      onChange={(e) => setStartDate(new Date(e.target.value))}
                      style={{
                        padding: ".3em",
                        width: "15%",
                        fontSize: "11px",
                        borderRadius: "0",
                        border: "1px solid #ccc",
                        borderColor: "black",
                      }}
                    />
                    <InputLabel
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        color: "var(--text-color)",
                        display: "inline-block !important",
                        marginBottom: "5px !important",
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      To Date: *
                    </InputLabel>
                    <Form.Control
                      type="date"
                      placeholder="To Date"
                      name="todate"
                      value={endDate ? endDate.toISOString().split("T")[0] : ""}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setEndDate(new Date(e.target.value))}
                      style={{
                        padding: ".3em",
                        width: "15%",
                        fontSize: "11px",
                        borderRadius: "0",
                        border: "1px solid #ccc",
                        borderColor: "black",
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyButton}
                      className="button apply-button"
                      data-testid="apply-button"
                    >
                      Apply
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleReset}
                      className="button reset-button"
                      data-testid="reset-apply-button"
                    >
                      Reset
                    </Button>
                  </div>

                  {/* Export to excel */}
                  <div
                    className="col-sm-1"
                    style={{
                      marginLeft: ".1em",
                      justifyContent: "right",
                      display: "flex",
                      alignItems: "center",
                      gap: ".5em",
                    }}
                  >
                    {gridRecordExist && (
                      <>
                        <Tooltip title="Click to save grid state">
                          <IconButton
                            onClick={handleSaveGridPreferences}
                            className="preference-icon"
                            data-testid="preference-icon"
                          >
                            <PushPinIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Click to export the grid content in excel">
                          <button
                            type="button"
                            onClick={handleExport}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                            }}
                            aria-label="Export to Excel"
                          >
                            <img
                              src={ExcelIcon}
                              alt="Doc-Distro"
                              className="excel-icon"
                              data-testid="excel-export"
                            />
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
                <div
                  className="col-sm-12"
                  style={{
                    display: "flex",
                    gap: ".5em",
                    alignItems: "center",
                    marginTop: "1em",
                    marginLeft: "4.5em",
                  }}
                >
                  <div
                    className="col-sm-8"
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "left",
                      alignItems: "left",
                    }}
                  >
                    <TextField
                      id="txtDDExceptions"
                      variant="outlined"
                      fullWidth
                      style={{
                        margin: "0em 0em 1em -.5em",
                        width: "37.5em",
                        fontSize: "11px",
                        borderRadius: "0",
                      }}
                      placeholder="Search"
                      type="text"
                      name="search"
                      value={searchContent}
                      onChange={handleInputChange}
                      onKeyDown={handleSearchKeyPress}
                      className="input-sm form-control col-sm-3"
                      required
                      InputProps={{
                        style: {
                          backgroundColor: "white",
                          height: "25px",
                          fontSize: "11px",
                        },
                      }}
                    ></TextField>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearchSubmit}
                      className="button search-button"
                      data-testid="search-button"
                    >
                      Search
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handelSearchContentClear}
                      className="button reset-button"
                      data-testid="reset-search-button"
                    >
                      Reset
                    </Button>
                  </div>
                </div>
                {/* AgGrid */}

                <div className="queue-table-view clearfix">
                  <div
                    className="ag-theme-quartz-dark"
                    style={{
                      height: "60vh",
                      width: "99%",
                      marginTop: ".1vh",
                    }}
                  >
                    <AgGridReact
                      columnDefs={columnDefs}
                      rowData={rowData}
                      ref={gridRef}
                      pagination={true}
                      paginationPageSize={defaulPageSize}
                      paginationPageSizeSelector={pageSizeSelector}
                      gridOptions={{
                        rowSelection: {
                          mode: "multiRow",
                          checkboxes: true,
                          headerCheckbox: true,
                          enableClickSelection: false,
                        },
                      }}
                      headerHeight={30}
                      rowGroupPanelShow="always"
                      pivotPanelShow="always"
                      overlayNoRowsTemplate={
                        '<span class="ag-overlay-loading-center">No rows to show</span>'
                      }
                      loading={!rowData || showLoader}
                      loadingOverlayComponent={CustomLoadingOverlay}
                      onGridReady={async (params) => {
                        set_GridApi(params.api);
                        const gridPreferences = await getUserGridPreferences();
                        if (gridPreferences) {
                          applyGridPreferences(gridPreferences);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* View RequestBody popup */}
                <div>
                  <Dialog
                    open={open}
                    fullWidth={true}
                    maxWidth="xl"
                    PaperProps={{ sx: { width: 400, height: 550 } }}
                  >
                    <DialogTitle
                      style={{
                        backgroundColor: "var(--primary-color) ",
                        height: "5em", // Set the background color of the dialog title
                      }}
                    >
                      <div
                        className="col-sm-12"
                        style={{
                          display: "flex",
                          justifyContent: "left",
                        }}
                      >
                        <div
                          className="col-sm-11"
                          style={{
                            display: "flex",
                            alignItems: "left",
                            gap: "7px",
                            color: "var(--aggrid-text-color)",
                            fontSize: "1.5em",
                          }}
                        >
                          Json Payload
                        </div>
                        <div
                          className="col-sm-1"
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "right",
                          }}
                        >
                          {" "}
                          <Tooltip title="Close">
                            <IconButton
                              onClick={handleClose}
                              style={{
                                cursor: "pointer",
                                alignItems: "flex-end",
                                color: "var(--aggrid-text-color)",
                                width: "20px",
                                marginTop: "-5px",
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                    </DialogTitle>
                    <DialogContent dividers>
                      <TextField
                        multiline
                        aria-label="minimum height"
                        maxRows={26}
                        style={{ width: "100%", overflow: "auto" }}
                        value={jsonPayloadValue}
                        aria-readonly
                        InputProps={{
                          style: {
                            backgroundColor: "#f9f9f9",
                            border: "1px solid #ccc",
                            font: "menu",
                            fontFamily: "monospace",
                          },
                        }}
                      ></TextField>
                      {copyValue ? (
                        <span style={{ color: "green", fontSize: "14px" }}>
                          {" "}
                          Copied text to clipboard.
                        </span>
                      ) : null}
                    </DialogContent>
                    <DialogActions>
                      <CopyToClipboard
                        text={jsonPayloadValue}
                        onCopy={handleCopyToClipboard}
                      >
                        <Button
                          className={styles.copytoclipboard}
                          style={{
                            textTransform: "none",
                            borderRadius: "0px",
                            background: "rgb(77, 123, 167) !important",
                            color: "#ddd !important",
                            fontSize: "11px",
                            padding: "5px 12px",
                            border: "1px solid #144979",
                            margin: "0 5px 5px 5px",
                            whiteSpace: "white-space",
                          }}
                        >
                          Copy to clipboard
                        </Button>
                      </CopyToClipboard>
                    </DialogActions>
                  </Dialog>
                </div>
              </div>
            </div>

            {/* <Row>
              <Col
                className="ag-theme-alpine"
                style={{ height: "calc(100vh - 250px)", width: "100%" }}
              >
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  pagination={true}
                  paginationPageSize={25}
                  animateRows={true}
                  domLayout="autoHeight"
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                    floatingFilter: true,
                  }}
                  rowSelection="single"
                />
              </Col>
            </Row> */}
          </Box>
        </Col>
      </Row>
    </div>
  );
};

export default ApiIngestion;
function showSnackbar(arg0: string, arg1: boolean) {
  throw new Error("Function not implemented.");
}
